import { makeAutoObservable, runInAction } from "mobx";
import { RootStore, ServiceStore } from "@intrasync/stores";

// Production Order interface based on the provided schema
export interface ProductionOrder {
  id: number;
  orderNumber: string;
  productionType?:
    | "Precast Panels"
    | "Structural Elements"
    | "Custom Components"
    | "Architectural Elements"
    | "Infrastructure"
    | "Other";
  status?:
    | "Planned"
    | "Scheduled"
    | "In Production"
    | "Quality Check"
    | "Completed"
    | "On Hold"
    | "Cancelled";
  priority?: "Low" | "Medium" | "High" | "Critical";

  // Relationships
  projectId?: number;
  bidId?: number;
  accountId?: number;

  // Production details
  productDescription?: string;
  quantity?: number;
  unitOfMeasure?: string;
  estimatedHours?: number;
  actualHours?: number;

  // Scheduling
  plannedStartDate?: string;
  plannedEndDate?: string;
  actualStartDate?: string;
  actualEndDate?: string;

  // Cost tracking
  estimatedCost?: number;
  actualCost?: number;
  materialCost?: number;
  laborCost?: number;

  // Progress tracking
  percentComplete?: number;
  qualityScore?: number;

  // Assignments
  assignedTo?: number;
  supervisorId?: number;

  // Additional details
  notes?: string;
  specialInstructions?: string;

  createdAt: string;
  updatedAt: string;
}

export class ProductionOrdersListStore {
  rootStore: RootStore;

  // Observables
  loading = false;
  error: string | null = null;
  filtersVisible = false;
  selectedProductionOrderIds: number[] = [];

  // Constructor
  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // Actions
  toggleFiltersVisibility() {
    this.filtersVisible = !this.filtersVisible;
  }

  toggleProductionOrderSelection(orderId: number) {
    const index = this.selectedProductionOrderIds.indexOf(orderId);
    if (index > -1) {
      this.selectedProductionOrderIds.splice(index, 1);
    } else {
      this.selectedProductionOrderIds.push(orderId);
    }
  }

  selectAllProductionOrders() {
    this.selectedProductionOrderIds = this.productionOrders.map(
      (order: any) => order.id
    );
  }

  clearSelection() {
    this.selectedProductionOrderIds = [];
  }

  // Async actions
  async loadProductionOrders() {
    runInAction(() => {
      this.loading = true;
    });

    try {
      await this.productionOrdersStore.find();
      runInAction(() => {
        this.error = null;
      });
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : "Failed to load production orders";
        console.error("Failed to load production orders:", error);
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async reloadProductionOrders() {
    await this.loadProductionOrders();
  }

  async createProductionOrder(orderData: Partial<ProductionOrder>) {
    this.loading = true;
    this.error = null;

    try {
      const result = await this.productionOrdersStore.create(orderData);
      runInAction(() => {
        this.error = null;
      });
      return result;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : "Failed to create production order";
        console.error("Failed to create production order:", error);
      });
      throw error;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async updateProductionOrder(
    orderId: number,
    orderData: Partial<ProductionOrder>
  ) {
    try {
      const result = await this.productionOrdersStore.patch(orderId, orderData);
      runInAction(() => {
        this.error = null;
      });
      return result;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : "Failed to update production order";
        console.error("Failed to update production order:", error);
      });
      throw error;
    }
  }

  async deleteProductionOrder(orderId: number) {
    try {
      await this.productionOrdersStore.remove(orderId);
      runInAction(() => {
        this.error = null;
      });
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : "Failed to delete production order";
        console.error("Failed to delete production order:", error);
      });
      throw error;
    }
  }

  async deleteProductionOrders(orderIds: number[]) {
    try {
      for (const orderId of orderIds) {
        await this.productionOrdersStore.remove(orderId);
      }
      runInAction(() => {
        this.error = null;
        this.selectedProductionOrderIds = [];
      });
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : "Failed to delete production orders";
        console.error("Failed to delete production orders:", error);
      });
      throw error;
    }
  }

  // Analytics
  getProductionOrderMetrics() {
    const totalOrders = this.productionOrders.length;
    const totalEstimatedCost = this.productionOrders.reduce(
      (sum, order) => sum + (order.estimatedCost || 0),
      0
    );
    const totalActualCost = this.productionOrders.reduce(
      (sum, order) => sum + (order.actualCost || 0),
      0
    );
    const totalEstimatedHours = this.productionOrders.reduce(
      (sum, order) => sum + (order.estimatedHours || 0),
      0
    );
    const totalActualHours = this.productionOrders.reduce(
      (sum, order) => sum + (order.actualHours || 0),
      0
    );

    const statusCounts = this.productionOrders.reduce((acc, order) => {
      const status = order.status || "Unknown";
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const priorityCounts = this.productionOrders.reduce((acc, order) => {
      const priority = order.priority || "Unknown";
      acc[priority] = (acc[priority] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const averageEstimatedCost =
      totalOrders > 0 ? totalEstimatedCost / totalOrders : 0;
    const costVariance =
      totalEstimatedCost > 0
        ? ((totalActualCost - totalEstimatedCost) / totalEstimatedCost) * 100
        : 0;
    const hoursVariance =
      totalEstimatedHours > 0
        ? ((totalActualHours - totalEstimatedHours) / totalEstimatedHours) * 100
        : 0;

    const avgPercentComplete =
      totalOrders > 0
        ? this.productionOrders.reduce(
            (sum, order) => sum + (order.percentComplete || 0),
            0
          ) / totalOrders
        : 0;

    const avgQualityScore =
      totalOrders > 0
        ? this.productionOrders.reduce(
            (sum, order) => sum + (order.qualityScore || 0),
            0
          ) / totalOrders
        : 0;

    return {
      totalOrders,
      totalEstimatedCost,
      totalActualCost,
      totalEstimatedHours,
      totalActualHours,
      averageEstimatedCost,
      costVariance,
      hoursVariance,
      statusCounts,
      priorityCounts,
      avgPercentComplete,
      avgQualityScore,
    };
  }

  // Computed values
  get productionOrdersStore(): ServiceStore {
    return this.rootStore.productionOrdersStore as ServiceStore;
  }

  get jobStore(): ServiceStore {
    return this.rootStore.jobStore as ServiceStore;
  }

  get customersStore(): ServiceStore {
    return this.rootStore.customersStore as ServiceStore;
  }

  get bidsStore(): ServiceStore | undefined {
    return this.rootStore.bidsStore as ServiceStore | undefined;
  }

  get productionOrders(): ProductionOrder[] {
    return this.productionOrdersStore
      .entities as unknown[] as ProductionOrder[];
  }

  get hasSelection(): boolean {
    return this.selectedProductionOrderIds.length > 0;
  }

  get isAllSelected(): boolean {
    return (
      this.selectedProductionOrderIds.length === this.productionOrders.length &&
      this.productionOrders.length > 0
    );
  }

  get hasFilters(): boolean {
    return false; // Filters are now handled by the datagrid
  }

  get availableProductionTypes(): string[] {
    return [
      ...new Set(
        this.productionOrders
          .map((order: any) => order.productionType)
          .filter(Boolean)
      ),
    ].sort();
  }

  get availableStatuses(): string[] {
    return [
      ...new Set(
        this.productionOrders.map((order: any) => order.status).filter(Boolean)
      ),
    ].sort();
  }

  get availablePriorities(): string[] {
    return [
      ...new Set(
        this.productionOrders.map((order: any) => order.priority).filter(Boolean)
      ),
    ].sort();
  }
}

export default ProductionOrdersListStore;
